/**
 * AISettingsPanel — AI Assistant Provider Configuration
 *
 * Same pattern as CloudinarySettingsPanel.js / EmailSettingsPanel.js.
 *
 * Add to PlatformSettings.js:
 *   import AISettingsPanel from './AISettingsPanel';
 *   // inside JSX, new Tab:
 *   <Tab eventKey="ai" title={<span><Bot size={14} className="me-1"/>AI Assistant</span>}>
 *       <div className="mt-1"><AISettingsPanel/></div>
 *   </Tab>
 *
 * API endpoints:
 *   GET  /superadmin/ai-settings
 *   PUT  /superadmin/ai-settings
 *   POST /superadmin/ai-settings/test
 */
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Badge, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { Bot, Cpu, Server, Eye, EyeOff, CheckCircle, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const AISettingsPanel = () => {
    const [loading,    setLoading]    = useState(true);
    const [saving,     setSaving]     = useState(false);
    const [testing,    setTesting]    = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [showAnthropicKey,  setShowAnthropicKey]  = useState(false);
    const [showSelfHostedKey, setShowSelfHostedKey] = useState(false);

    const [form, setForm] = useState({
        ai_enabled:           true,
        ai_provider_mode:     'anthropic',
        anthropic_api_key:    '',
        anthropic_model:      'claude-sonnet-4-6',
        self_hosted_endpoint: '',
        self_hosted_api_key:  '',
        self_hosted_model:    '',
    });

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        axiosInstance.get('/superadmin/ai-settings')
            .then(r => {
                const d = r.data.data || {};
                setForm({
                    ai_enabled:           d.ai_enabled !== false,
                    ai_provider_mode:     d.ai_provider_mode     || 'anthropic',
                    anthropic_api_key:    d.anthropic_api_key    || '',
                    anthropic_model:      d.anthropic_model      || 'claude-sonnet-4-6',
                    self_hosted_endpoint: d.self_hosted_endpoint || '',
                    self_hosted_api_key:  d.self_hosted_api_key  || '',
                    self_hosted_model:    d.self_hosted_model    || '',
                });
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const save = async () => {
        setSaving(true);
        setTestResult(null);
        try {
            await axiosInstance.put('/superadmin/ai-settings', form);
            toast.success('AI Assistant settings saved!');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save');
        } finally { setSaving(false); }
    };

    const test = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await axiosInstance.post('/superadmin/ai-settings/test');
            setTestResult({ ok: true, message: res.data.message });
            toast.success('Test passed ✓');
        } catch (err) {
            setTestResult({ ok: false, error: err.response?.data?.message || 'Test failed' });
            toast.error(err.response?.data?.message || 'Test failed');
        } finally { setTesting(false); }
    };

    const mode = form.ai_provider_mode;
    const isAnthropic  = mode === 'anthropic';
    const isRuleBased  = mode === 'rule_based';
    const isSelfHosted = mode === 'self_hosted';

    if (loading) return (
        <div className="text-center py-4">
            <Spinner animation="border" size="sm" variant="primary"/>
        </div>
    );

    return (
        <div>
            {/* ── Header ─────────────────────────────────────────────── */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                    <h5 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <Bot size={20} className="text-primary"/>
                        AI Assistant Settings
                    </h5>
                    <p className="text-muted small mb-0">
                        Choose how the HR Chatbot understands and answers questions.
                        Currently active:{' '}
                        <Badge bg={isAnthropic ? 'success' : isRuleBased ? 'secondary' : 'info'}>
                            {isAnthropic ? '🤖 AI (I2Speed Cortex)' : isRuleBased ? '⚙ Rule-Based' : '🖥 Self-Hosted'}
                        </Badge>
                    </p>
                </div>
                <Form.Check
                    type="switch"
                    id="ai-enabled-switch"
                    checked={!!form.ai_enabled}
                    label={<span className="fw-bold">
                        {form.ai_enabled ? 'Enabled' : 'Disabled'}
                    </span>}
                    onChange={e => set('ai_enabled', e.target.checked)}
                />
            </div>
            {!form.ai_enabled && (
                <Alert variant="warning" className="small py-2 mb-4">
                    AI Assistant is disabled. The chatbot will not respond to any user across the platform.
                </Alert>
            )}

            {/* ── Provider selector ──────────────────────────────────── */}
            <Row className="g-3 mb-4">
                {/* I2Speed Cortex */}
                <Col xs={12} md={4}>
                    <div
                        onClick={() => set('ai_provider_mode', 'anthropic')}
                        style={{
                            border: `2px solid ${isAnthropic ? '#4f46e5' : '#e5e7eb'}`,
                            borderRadius: 12, padding: '18px 20px', cursor: 'pointer',
                            background: isAnthropic ? '#eef2ff' : '#fff',
                            transition: 'all 0.2s', height: '100%',
                        }}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="d-flex align-items-center gap-2">
                                <Bot size={20} className="text-primary"/>
                                <div>
                                    <div className="fw-bold">AI</div>
                                    <div className="text-muted small">I2Speed Cortex API</div>
                                </div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                <Badge bg="success" style={{ fontSize: 10 }}>Recommended</Badge>
                                {isAnthropic && <CheckCircle size={16} color="#4f46e5"/>}
                            </div>
                        </div>
                        <ul className="small text-muted mb-0 ps-3">
                            <li>Best understanding of free-typed questions</li>
                            <li>Can chain multiple tools per question</li>
                            <li>Writes documents (offer letters, etc.)</li>
                            <li className="text-muted">Pay-per-token — typically a few cents/month</li>
                        </ul>
                    </div>
                </Col>
                {/* Self-hosted */}
                <Col xs={12} md={4}>
                    <div
                        onClick={() => set('ai_provider_mode', 'self_hosted')}
                        style={{
                            border: `2px solid ${isSelfHosted ? '#4f46e5' : '#e5e7eb'}`,
                            borderRadius: 12, padding: '18px 20px', cursor: 'pointer',
                            background: isSelfHosted ? '#eef2ff' : '#fff',
                            transition: 'all 0.2s', height: '100%',
                        }}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="d-flex align-items-center gap-2">
                                <Server size={20} className="text-info"/>
                                <div>
                                    <div className="fw-bold">Self-Hosted</div>
                                    <div className="text-muted small">Your own LLM server</div>
                                </div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                <Badge bg="warning" text="dark" style={{ fontSize: 10 }}>Build later</Badge>
                                {isSelfHosted && <CheckCircle size={16} color="#4f46e5"/>}
                            </div>
                        </div>
                        <ul className="small text-muted mb-0 ps-3">
                            <li>No data leaves your infrastructure</li>
                            <li>No per-token cost</li>
                            <li>Requires a GPU server (Llama, Mistral, etc.)</li>
                            <li className="text-warning">⚠ Endpoint not configured yet — stubbed</li>
                        </ul>
                    </div>
                </Col>
                {/* Rule-based */}
                <Col xs={12} md={4}>
                    <div
                        onClick={() => set('ai_provider_mode', 'rule_based')}
                        style={{
                            border: `2px solid ${isRuleBased ? '#4f46e5' : '#e5e7eb'}`,
                            borderRadius: 12, padding: '18px 20px', cursor: 'pointer',
                            background: isRuleBased ? '#eef2ff' : '#fff',
                            transition: 'all 0.2s', height: '100%',
                        }}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="d-flex align-items-center gap-2">
                                <Cpu size={20} className="text-secondary"/>
                                <div>
                                    <div className="fw-bold">Rule-Based</div>
                                    <div className="text-muted small">No LLM at all</div>
                                </div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                <Badge bg="secondary" style={{ fontSize: 10 }}>Zero cost</Badge>
                                {isRuleBased && <CheckCircle size={16} color="#4f46e5"/>}
                            </div>
                        </div>
                        <ul className="small text-muted mb-0 ps-3">
                            <li>No external API, no ongoing cost</li>
                            <li>Works great for the Quick Action buttons</li>
                            <li>Weaker on free-typed phrasing</li>
                            <li className="text-muted">Can't draft documents (needs an LLM)</li>
                        </ul>
                    </div>
                </Col>
            </Row>

            {/* ── I2Speed Cortex credentials ──────────────────────────────── */}
            {isAnthropic && (
                <Card className="border-0 shadow-sm p-4 mb-4">
                    <div className="fw-bold small text-uppercase text-muted mb-3">
                        AI Configuration
                    </div>
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">
                                API Key <span className="text-danger">*</span>
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showAnthropicKey ? 'text' : 'password'}
                                    placeholder="sk-ant-xxxxxxxxxxxxxxxxxxxxxxxxxx"
                                    value={form.anthropic_api_key}
                                    onChange={e => set('anthropic_api_key', e.target.value)}
                                />
                                <Button variant="outline-secondary" type="button"
                                    onClick={() => setShowAnthropicKey(p => !p)}>
                                    {showAnthropicKey ? <EyeOff size={14}/> : <Eye size={14}/>}
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                Get your API key from{' '}
                                <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer">
                                    console.anthropic.com
                                </a>
                            </Form.Text>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Model</Form.Label>
                            <Form.Select
                                value={form.anthropic_model}
                                onChange={e => set('anthropic_model', e.target.value)}>
                                <option value="claude-sonnet-4-6">claude-sonnet-4-6 (recommended)</option>
                                <option value="claude-opus-4-7">claude-opus-4-7 (highest quality, slower)</option>
                                <option value="claude-haiku-4-5-20251001">claude-haiku-4-5 (fastest, cheapest)</option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Sonnet is the best balance of quality, speed, and cost for HR Q&amp;A.
                            </Form.Text>
                        </Col>
                    </Row>
                </Card>
            )}

            {/* ── Self-hosted config ──────────────────────────────────── */}
            {isSelfHosted && (
                <Card className="border-0 shadow-sm p-4 mb-4">
                    <div className="fw-bold small text-uppercase text-muted mb-3">
                        Self-Hosted Server Configuration
                    </div>
                    <Alert variant="info" className="small py-2 mb-3">
                        This mode is stubbed for now — saving an endpoint here will let the
                        Assistant try to reach it, but no self-hosted server is required to
                        use PeoplesDock today. Switch to AI or Rule-Based until you've
                        deployed your own LLM server.
                    </Alert>
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Endpoint URL</Form.Label>
                            <Form.Control
                                placeholder="http://your-server:8000/v1/chat"
                                value={form.self_hosted_endpoint}
                                onChange={e => set('self_hosted_endpoint', e.target.value)}
                            />
                            <Form.Text className="text-muted">
                                Base URL of your self-hosted LLM server (vLLM, Ollama, text-generation-webui, etc.)
                            </Form.Text>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">API Key (optional)</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSelfHostedKey ? 'text' : 'password'}
                                    placeholder="Leave blank if your server has no auth"
                                    value={form.self_hosted_api_key}
                                    onChange={e => set('self_hosted_api_key', e.target.value)}
                                />
                                <Button variant="outline-secondary" type="button"
                                    onClick={() => setShowSelfHostedKey(p => !p)}>
                                    {showSelfHostedKey ? <EyeOff size={14}/> : <Eye size={14}/>}
                                </Button>
                            </InputGroup>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Model Name</Form.Label>
                            <Form.Control
                                placeholder="e.g. llama-3-8b-instruct"
                                value={form.self_hosted_model}
                                onChange={e => set('self_hosted_model', e.target.value)}
                            />
                            <Form.Text className="text-muted">
                                Model identifier as known to your self-hosted server.
                            </Form.Text>
                        </Col>
                    </Row>
                </Card>
            )}

            {/* ── Rule-based info ─────────────────────────────────────── */}
            {isRuleBased && (
                <Card className="border-0 shadow-sm p-4 mb-4">
                    <div className="fw-bold small text-uppercase text-muted mb-3">
                        How Rule-Based Mode Works
                    </div>
                    <p className="small text-muted mb-2">
                        No API key or external server needed. The Assistant matches keywords
                        in each message against the 12 built-in HR tools (attendance, leave,
                        payroll, helpdesk, expenses, surveys, etc.) and replies using a
                        hand-written template for whichever tool matched best.
                    </p>
                    <p className="small text-muted mb-0">
                        This works reliably for the fixed Quick Action buttons, since those use
                        exact known phrasing. Free-typed questions will work for common phrasings
                        but may not be understood if worded unusually — in that case the Assistant
                        will list what it can help with instead of guessing.
                    </p>
                </Card>
            )}

            {/* Test result */}
            {testResult && (
                <Alert variant={testResult.ok ? 'success' : 'danger'} className="small py-2 mb-3">
                    {testResult.ok ? <>✓ {testResult.message}</> : <>✗ {testResult.error}</>}
                </Alert>
            )}

            {/* ── Action buttons ─────────────────────────────────────── */}
            <div className="d-flex gap-3 align-items-center flex-wrap">
                <Button variant="primary" onClick={save} disabled={saving}>
                    {saving ? <><Spinner size="sm" className="me-2"/>Saving…</> : '💾 Save Settings'}
                </Button>
                <Button variant="outline-info" onClick={test} disabled={testing}>
                    {testing
                        ? <><Spinner size="sm" className="me-2"/>Testing…</>
                        : <><RefreshCw size={13} className="me-1"/>Test Connection</>}
                </Button>
            </div>

            {/* Info box */}
            <Alert variant="light" className="mt-4 mb-0 border small">
                <strong>How it works:</strong> All three modes answer using the SAME 12 HR
                data tools (attendance, leave, payroll, helpdesk, expenses, surveys, employee
                search, and more) — only how the Assistant understands your question and
                phrases its reply changes. Switching modes takes effect immediately, no
                restart required.
            </Alert>
        </div>
    );
};

export default AISettingsPanel;
